/**
 * Hero Section Component
 *
 * Full-viewport promotional banner. The hero fills the remaining viewport
 * height below the header (100svh minus header) so nothing else is visible
 * on initial load. Uses object-fit: cover to crop the image to fit.
 *
 * All props are optional — the component renders identically with no props
 * passed, falling back to the built-in default image URLs.
 */

'use client';

import { useState } from 'react';
import styled from '@emotion/styled';
import { isAllowedHref } from '@/lib/utils/is-allowed-href';

const DESKTOP_SRC =
  'https://cdn.shapermint.com/assets/shapermint/images/transition/valentines-day-sale/Nova_main_desk.jpg';
const MOBILE_SRC =
  'https://cdn.shapermint.com/external/cdnshopifycom/images/s/files/1/0021/4889/2732/files/Nova_main_mob_2x_be9b0010-232b-4783-9b2a-02e6908ddc85.jpg?quality=100&url=https%3A%2F%2Fcdn.shopify.com%2Fs%2Ffiles%2F1%2F0021%2F4889%2F2732%2Ffiles%2FNova_main_mob_2x_be9b0010-232b-4783-9b2a-02e6908ddc85.jpg%3Fv%3D1772717333';

export interface HeroSectionProps {
  desktopSrc?: string;
  mobileSrc?: string;
  headline?: string;
  ctaText?: string;
  /** Full href for the CTA button. For internal paths in localized storefronts,
   *  pass the already-localized URL (e.g. via `localizedPath('/products')`).
   *  Only http(s), relative paths, and hash links are allowed — unsafe schemes
   *  like `javascript:` are silently rejected. */
  ctaHref?: string;
  alt?: string;
}

const BannerContainer = styled.section`
  position: relative;
  width: 100%;
  height: calc(100svh - 100px);
  overflow: hidden;

  picture {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

interface HeroImageProps {
  $loaded: boolean;
}

const BannerImage = styled.img<HeroImageProps>`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center top;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    opacity: 1;
  }
`;

const Overlay = styled.div`
  /* pointer-events: none allows clicks to pass through to the banner image.
     Interactive children (CtaLink) restore pointer-events: all individually. */
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  pointer-events: none;
`;

const Headline = styled.h1`
  color: #ffffff;
  font-size: clamp(1.5rem, 4vw, 3rem);
  font-weight: 700;
  text-align: center;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
  margin: 0;
  padding: 0 16px;
`;

const CtaLink = styled.a`
  pointer-events: all;
  display: inline-block;
  padding: 12px 32px;
  background-color: #ffffff;
  color: #000000;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus-visible {
    background-color: #f0f0f0;
  }

  &:focus-visible {
    outline: 3px solid #1a1a1a;
    outline-offset: 3px;
  }
`;

export function HeroSection({
  desktopSrc = DESKTOP_SRC,
  mobileSrc = MOBILE_SRC,
  headline,
  ctaText,
  ctaHref,
  alt = 'Hero banner',
}: HeroSectionProps = {}) {
  const [imageLoaded, setImageLoaded] = useState(false);

  const safeCtaHref = ctaHref && isAllowedHref(ctaHref) ? ctaHref : undefined;
  const showCta = Boolean(ctaText && safeCtaHref);
  const hasOverlay = Boolean(headline || showCta);

  return (
    <BannerContainer data-testid="hero-banner">
      <picture>
        <source media="(max-width: 640px)" srcSet={mobileSrc} />
        <source media="(min-width: 641px)" srcSet={desktopSrc} />
        <BannerImage
          src={desktopSrc}
          alt={alt}
          loading="eager"
          $loaded={imageLoaded}
          onLoad={() => setImageLoaded(true)}
        />
      </picture>

      {hasOverlay && (
        <Overlay>
          {headline && <Headline>{headline}</Headline>}
          {showCta && (
            <CtaLink href={safeCtaHref!}>
              {ctaText}
            </CtaLink>
          )}
        </Overlay>
      )}
    </BannerContainer>
  );
}
